/**
 * Shared badge styling. ONE source, so a badge's shape can't drift the way it
 * did through the 2026-09-03 redesign — the same "category tag" existed as five
 * hand-rolled `<span>`s (profile primary, profile secondary, search-card tab,
 * featured-card tab, homepage step, insight tag) and each had to be corrected
 * separately, twice.
 *
 * ⛔ RADIUS IS SCALED TO THE ELEMENT, NOT TO THE SYSTEM. These badges are
 * ~20px tall, so `--radius-control` (8px) is 40% of their own height and reads
 * as a pill. They use `--radius-md` (4px). `rounded-full` is reserved for
 * things that are MEANT to be pills — chat suggestion chips, the homepage
 * service chips — which are taller and interactive.
 *
 * Mirrors the `form-styles.ts` idiom: plain exported class strings, no runtime.
 */

/** Shape + type ramp shared by every badge. Never set a radius outside this. */
export const BADGE_BASE =
  "inline-flex items-center rounded-md text-[0.6rem] font-bold uppercase leading-none tracking-[0.1em]";

/** Primary category — solid gold. The listing's own category. */
export const BADGE_CATEGORY = `${BADGE_BASE} bg-gold px-8 py-[5px] text-navy`;

/** Secondary categories — the same tag, de-emphasised. */
export const BADGE_CATEGORY_MUTED = `${BADGE_BASE} bg-gold/20 px-8 py-[5px] text-navy`;

/** Neutral tag on a light surface — step numbers, counts. */
export const BADGE_NEUTRAL = `${BADGE_BASE} bg-navy/[0.06] px-8 py-[4px] text-navy/70`;

/**
 * Where a partner referral came from (partner-referral-ids-spec.md 5.1; picked
 * in design/partner-referrals-mockup.html, badge variant 1). A PILL, like the
 * trust markers and unlike BADGE_BASE's tag corner: the owner picked the pill,
 * and it reads as a marker of who brought the client, not as a category.
 * BADGE_MATCH: a referral our team made (request or by hand), solid gold, with
 * the Easytiger mark (MaskIcon, /EasyTiger_BadgeOnly_Black&White.svg, size 12).
 * BADGE_REFERRAL_NEUTRAL: a referral that came through the profile's form.
 */
const BADGE_PILL = "inline-flex items-center gap-5 whitespace-nowrap rounded-full text-3xs font-bold uppercase leading-none tracking-wider";
export const BADGE_MATCH = `${BADGE_PILL} bg-gold py-5 pl-6 pr-9 text-navy`;
export const BADGE_REFERRAL_NEUTRAL = `${BADGE_PILL} bg-navy/[0.06] px-8 py-5 text-navy/70`;
/** The mark inside BADGE_MATCH. */
export const MATCH_MARK_SRC = "/EasyTiger_BadgeOnly_Black&White.svg";

/**
 * A tag that BLEEDS to the CARD's left edge (search + grid cards), crossing the
 * gutter and overlapping the lower third of the photo. Only the free corner is
 * rounded — the flush edge stays square, which is what keeps it reading as an
 * editorial flag pinned to the card rather than a sticker floating on a photo.
 *
 * ⚠️ THE CALLER OWNS THE ANCHOR, and getting it wrong fails SILENTLY. The tab
 * must be a SIBLING of the photo well, not a child: every well is
 * `overflow-hidden rounded-control`, so a tab inside one is simply cut off at
 * the photo's edge and reads as the old photo-flush design. Both call sites
 * therefore split the photo into an outer positioning box (no clip, carries the
 * gutter) and an inner clipping box, and give the tab a NEGATIVE left inset
 * equal to that gutter — which differs per card, so it cannot live here.
 *
 * The caller also caps the width, at 70% ACROSS THE PHOTO (owner, 2026-09-03):
 * past that a label reaches over the subject's face, which Vietnamese does
 * routinely. Wrapping needs `leading-tight` — `BADGE_BASE` is `leading-none`,
 * which collapses two lines into each other.
 */
export const BADGE_TAB = `${BADGE_BASE} rounded-l-none rounded-r-md px-8 py-5`;

/** The gold variant of the bleeding tab. */
export const BADGE_TAB_CATEGORY = `${BADGE_TAB} bg-gold text-navy`;

/** The "hidden listing" state tab — same shape, admin-facing colour. */
export const BADGE_TAB_HIDDEN = `${BADGE_TAB} gap-[4px] bg-gray-800/90 text-white`;

/**
 * Outlined TRUST markers (Expert Verified, Founding Member) — a different
 * object from a category tag, and deliberately still `rounded-full`.
 *
 * Owner call, 2026-09-03: the pill shape was only wrong on the CATEGORY tags.
 * These earn the pill because they are taller (py-3 + 10px text + a leading
 * icon, ~28px vs the tags' ~20px, so the radius is a much smaller fraction of
 * the height), they carry an icon, and reading as a distinct SHAPE from the
 * category tags is the point — it is what separates "we verified this" from
 * "this is the category".
 */
export const BADGE_OUTLINE =
  "inline-flex items-center gap-8 rounded-full border px-13 py-3 text-[10px] font-bold uppercase tracking-widest text-navy";

/**
 * A pill LINK: the homepage category pills and the landing pages' "By city" /
 * "By need" chips. A navigation control, not a label, so it is a pill meant to
 * be one (`rounded-full`, see the radius rule in CLAUDE.md) and it darkens its
 * border on hover. Extracted 2026-09-18 when the landing chips would have been
 * its second inline copy.
 */
export const PILL_LINK =
  "group inline-flex items-center gap-6 rounded-full border border-navy/15 bg-white px-13 py-5 text-sm text-gray-600 transition-colors hover:border-navy hover:text-navy";
